// Global upload service for managing background file uploads
class UploadService {
  constructor() {
    this.uploads = new Map(); // Map<uploadId, uploadState>
    this.listeners = new Map(); // Map<uploadId, Set<listeners>>
    this.nextUploadId = 1;
  }

  // Generate unique upload ID
  generateUploadId() {
    return `upload_${Date.now()}_${this.nextUploadId++}`;
  }

  // Register a listener for upload updates
  addListener(uploadId, callback) {
    if (!this.listeners.has(uploadId)) {
      this.listeners.set(uploadId, new Set());
    }
    this.listeners.get(uploadId).add(callback);
  }

  // Remove a listener
  removeListener(uploadId, callback) {
    if (this.listeners.has(uploadId)) {
      this.listeners.get(uploadId).delete(callback);
      if (this.listeners.get(uploadId).size === 0) {
        this.listeners.delete(uploadId);
      }
    }
  }

  // Notify all listeners for an upload
  notifyListeners(uploadId, data) {
    if (this.listeners.has(uploadId)) {
      this.listeners.get(uploadId).forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error("Error in upload listener:", error);
        }
      });
    }
  }

  // Create a new upload job
  createUpload(uploadData) {
    const uploadId = this.generateUploadId();
    const uploadState = {
      id: uploadId,
      files: uploadData.files || [],
      categories: uploadData.categories || [],
      status: "pending", // pending, uploading, completed, failed, cancelled
      progress: {},
      overallProgress: 0,
      completedFiles: [],
      failedFiles: [],
      metadata: uploadData.metadata || {}, // Store message data, conversationId, etc.
      createdAt: Date.now(),
      onComplete: uploadData.onComplete,
      onError: uploadData.onError,
      onProgress: uploadData.onProgress,
    };

    this.uploads.set(uploadId, uploadState);
    this.persistState();
    return uploadId;
  }

  // Update upload progress
  updateProgress(uploadId, fileIndex, progress) {
    const upload = this.uploads.get(uploadId);
    if (!upload) return;

    upload.progress[fileIndex] = progress;

    // Calculate overall progress
    const totalFiles = upload.files.length;
    if (totalFiles > 0) {
      const totalProgress = Object.values(upload.progress).reduce(
        (sum, p) => sum + p,
        0
      );
      upload.overallProgress = Math.round(totalProgress / totalFiles);
    }

    this.persistState();
    this.notifyListeners(uploadId, {
      type: "progress",
      upload: { ...upload },
    });

    if (upload.onProgress) {
      upload.onProgress(upload.overallProgress, upload.progress);
    }
  }

  // Mark a file as completed
  markFileCompleted(uploadId, fileIndex, result) {
    const upload = this.uploads.get(uploadId);
    if (!upload) return;

    upload.completedFiles.push({ fileIndex, result });
    upload.progress[fileIndex] = 100;

    // Check if all files are completed
    if (upload.completedFiles.length === upload.files.length) {
      upload.status = "completed";
      this.persistState();
      this.notifyListeners(uploadId, {
        type: "completed",
        upload: { ...upload },
      });

      if (upload.onComplete) {
        upload.onComplete(upload.completedFiles.map((f) => f.result));
      }
    } else {
      this.persistState();
      this.notifyListeners(uploadId, {
        type: "fileCompleted",
        upload: { ...upload },
      });
    }
  }

  // Mark a file as failed
  markFileFailed(uploadId, fileIndex, error) {
    const upload = this.uploads.get(uploadId);
    if (!upload) return;

    upload.failedFiles.push({ fileIndex, error });
    upload.status = "failed";
    this.persistState();
    this.notifyListeners(uploadId, {
      type: "failed",
      upload: { ...upload },
    });

    if (upload.onError) {
      upload.onError(error, upload);
    }
  }

  // Update upload status
  updateStatus(uploadId, status) {
    const upload = this.uploads.get(uploadId);
    if (!upload) return;

    upload.status = status;
    this.persistState();
    this.notifyListeners(uploadId, {
      type: "status",
      upload: { ...upload },
    });
  }

  // Get upload state
  getUpload(uploadId) {
    return this.uploads.get(uploadId);
  }

  // Get all active uploads
  getActiveUploads() {
    return Array.from(this.uploads.values()).filter(
      (upload) =>
        upload.status === "pending" || upload.status === "uploading"
    );
  }

  // Remove upload
  removeUpload(uploadId) {
    this.uploads.delete(uploadId);
    this.listeners.delete(uploadId);
    this.persistState();
  }

  // Cancel upload
  cancelUpload(uploadId) {
    const upload = this.uploads.get(uploadId);
    if (!upload) return;

    upload.status = "cancelled";
    this.persistState();
    this.notifyListeners(uploadId, {
      type: "cancelled",
      upload: { ...upload },
    });
  }

  // Persist state to localStorage (for navigation persistence)
  persistState() {
    try {
      const stateToPersist = Array.from(this.uploads.entries())
        .filter(([_, upload]) => upload.status === "uploading")
        .map(([id, upload]) => ({
          id,
          metadata: upload.metadata,
          status: upload.status,
          overallProgress: upload.overallProgress,
          createdAt: upload.createdAt,
        }));

      localStorage.setItem(
        "uploadService_state",
        JSON.stringify(stateToPersist)
      );
    } catch (error) {
      console.error("Failed to persist upload state:", error);
    }
  }

  // Restore state from localStorage
  restoreState() {
    try {
      const persisted = localStorage.getItem("uploadService_state");
      if (persisted) {
        const state = JSON.parse(persisted);
        // Note: We don't restore full upload state, just metadata
        // Active uploads should be managed by components
        return state;
      }
    } catch (error) {
      console.error("Failed to restore upload state:", error);
    }
    return [];
  }

  // Clear persisted state
  clearPersistedState() {
    try {
      localStorage.removeItem("uploadService_state");
    } catch (error) {
      console.error("Failed to clear persisted state:", error);
    }
  }
}

// Export singleton instance
export const uploadService = new UploadService();
